import { faBan, faEdit, faFileInvoice, faGlasses, faTable, faUndo } from '@fortawesome/free-solid-svg-icons';

import { Action, Column, DataTable, DataTableContainer, Filter, TableActionButton } from 'components/DataTable';
import { PaymentStatusButton } from 'components/MollieConnect';
import { SpaceBookingEditForm } from 'components/SpaceBooking/SpaceBooking/SpaceBookingEditForm';
import { useModal } from 'hooks/useModal';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ManualSpaceBookingForm, SpaceBookingCancellationForm, SpaceBookingPreview } from '..';

import { Button } from '@ui';
import { cx } from 'class-variance-authority';
import { ActionCallbackProps } from 'components/DataTable/DataTable';
import useFileDownload from 'hooks/useFileDownload';
import { useApi } from '../../../../hooks/useApi';
import { useConfirmedCallback } from '../../../../hooks/useConfirmedCallback';
import { SpaceBooking } from '../../types';
import { SpaceBookingDatesComponent } from './SpaceBookingDatesComponent';
import { SpaceBookingPreviewComponent } from './SpaceBookingPreviewComponent';

interface Props {
    clubSlug: string;
}

export const SpaceBookingList = ({ clubSlug }: Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [setModal, resetModal] = useModal();
    const asyncList = useRef<any>(undefined);
    const api = useApi();
    const download = useFileDownload();

    const handleShowSpaceBookingPreview = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}"`,
                content: <SpaceBookingPreview booking={booking} />,
                show: true,
                size: 60
            }),
        []
    );

    const handleEditSpaceBooking = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}" bearbeiten`,
                content: (
                    <SpaceBookingEditForm
                        adminMode
                        booking={booking}
                        clubSlug={clubSlug}
                        callback={() => {
                            asyncList.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 70
            }),
        []
    );

    const handleCancelSpaceBooking = useCallback(
        (booking: SpaceBooking) =>
            setModal({
                title: `Buchung von "${booking.user_name}"`,
                content: (
                    <SpaceBookingCancellationForm
                        booking={booking}
                        callback={() => {
                            asyncList.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true
            }),
        []
    );

    const handleRevokeCancellation = useConfirmedCallback((booking: SpaceBooking) => {
        void api({
            url: `space/booking/${booking.id}/revoke`,
            method: 'PUT',
            onSuccess: () => asyncList.current?.reload()
        });
    });

    const handleShowManualSpaceBookingForm = useCallback(
        () =>
            setModal({
                title: 'Manuelle Buchung hinzufügen',
                content: (
                    <ManualSpaceBookingForm
                        clubSlug={clubSlug}
                        callback={() => {
                            asyncList.current?.reload();
                            resetModal();
                        }}
                    />
                ),
                show: true,
                size: 70
            }),
        []
    );

    const handleDownloadCSV = useCallback(
        ({ selection, filter }: ActionCallbackProps) => {
            setIsLoading(true);
            api({
                url: `space/booking/${clubSlug}/csv`,
                method: 'POST',
                data: { selection, filter: { ...filter, status: { value: 1 } } },
                onBefore: () => setIsLoading(false),
                onSuccess: (data) => download(data.download_id)
            });
        },
        [download]
    );

    const handleCreateInvoices = useCallback(({ selection, filter }: ActionCallbackProps) => {
        setIsLoading(true);
        api({
            url: `space/booking/${clubSlug}/invoice`,
            method: 'POST',
            data: { selection, filter: { ...filter, status: { value: 1 } } },
            onBefore: () => setIsLoading(false),
            onSuccess: () => alert('Deine Rechnungen werden erstellt, der Vorgang kann einen Moment dauern!')
        });
    }, []);

    const columns: Column<SpaceBooking>[] = [
        {
            uid: 'user_name',
            name: 'Benutzer',
            renderer(_, item?) {
                return <SpaceBookingPreviewComponent booking={item} />;
            }
        },
        {
            uid: 'start',
            name: 'Daten',
            allowsSorting: true,
            renderer(_, item?) {
                return <SpaceBookingDatesComponent booking={item} />;
            }
        },
        {
            uid: 'spaces',
            name: 'Plätze',
            allowsSorting: true,
            renderer(value) {
                return (
                    <ul className="tw-m-0 tw-list-none tw-p-0">
                        {value?.map((space) => (
                            <li className="tw-m-0 tw-p-0" key={space.id}>
                                <p className="tw-font-xs tw-m-0 tw-p-0 tw-font-extralight">{space.name}</p>
                            </li>
                        ))}
                    </ul>
                );
            }
        },
        {
            uid: 'price',
            name: 'Zahlungsinfos',
            renderer(value, item?) {
                return (
                    <div className="tw-flex tw-justify-between">
                        <span className={cx(item.voucher ? '!tw-line-through' : '')}>
                            {value !== null ? `${value} €` : ''}
                        </span>
                        {item.usedVouchers?.length > 0 ? ` (Gutschein: #${item.usedVouchers?.join(', ')})` : ''}
                        {item.voucher ? ` (Karte: #${item.voucher.id})` : ''}
                        {item.paymentId && (
                            <PaymentStatusButton paymentId={item.paymentId} paymentStatus={item.paymentStatus} />
                        )}
                    </div>
                );
            }
        },
        {
            uid: 'actions',
            name: 'Optionen',
            align: 'end'
        }
    ];

    const filters: Filter[] = [
        {
            filterName: 'Nummern',
            filterKey: 'ids',
            filterPlaceholder: 'Trenne mehrere Nummern mit Komma um diese gleichzeitig anzuzeigen',
            filterType: 'text'
        },
        { filterName: 'Plätze', filterKey: 'spaces', filterType: 'text' },
        { filterName: 'Startdatum', filterKey: 'start', filterType: 'date' },
        { filterName: 'Storniert am', filterKey: 'cancelled_at', filterType: 'date' },
        { filterName: 'Gebucht am', filterKey: 'created_at', filterType: 'date' },
        { filterName: 'Preis', filterKey: 'price', filterType: 'numeric' },
        { filterName: 'Benutzer E-Mail', filterKey: 'email', filterType: 'text' },
        { filterName: 'Platzabo', filterKey: 'subscriptions', filterType: 'text' },
        { filterName: 'Stornierungsgrund', filterKey: 'cancellation_reason', filterType: 'text' }
    ];

    const actions = useMemo(
        (): Action[] => [
            { key: 'csv', label: 'Als CSV exportieren', icon: faTable, action: handleDownloadCSV },
            { key: 'invoice', label: 'Rechnungen generieren', icon: faFileInvoice, action: handleCreateInvoices }
        ],
        [handleDownloadCSV, handleCreateInvoices]
    );

    return (
        <DataTableContainer>
            <Button className="tw-py-5" css={{ width: '100%' }} onPress={() => handleShowManualSpaceBookingForm()}>
                Manuelle Buchung erstellen
            </Button>
            <DataTable
                actions={actions}
                actionsLoading={isLoading}
                asyncListRef={asyncList}
                columns={columns}
                filters={filters}
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false,
                    selectionMode: 'multiple'
                }}
                fetchUrl={`space/booking/${clubSlug}`}
                rowActions={(item) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton icon={faGlasses} onClick={() => handleShowSpaceBookingPreview(item)} />
                        {!item.cancelled_at ? (
                            <>
                                <TableActionButton
                                    color="warning"
                                    icon={faEdit}
                                    onClick={() => handleEditSpaceBooking(item)}
                                />
                                <TableActionButton
                                    color="error"
                                    icon={faBan}
                                    onClick={() => handleCancelSpaceBooking(item)}
                                />
                            </>
                        ) : (
                            <TableActionButton
                                color="error"
                                icon={faUndo}
                                onClick={() => handleRevokeCancellation(item)}
                            />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default SpaceBookingList;
